import '@design'

import Vue from 'vue'
import App from './App.vue'
import { sync } from 'vuex-router-sync'
import router from '@router'
import store from '@store' // done. Returns an unsync callback fn
import '@plugins'

import '@utils/registerServiceWorker'
import '@utils/registerSharedComponents'

import { initHeartbeat, startHeartbeat, stopHeartbeat } from '@utils/heartbeat'

import Notifications from 'vue-notification'
import vueScrollto from 'vue-scrollto'

import VueMq from 'vue-mq'

import 'vuetify/dist/vuetify.min.css'

import Vuetify, {
  VApp, // required
  VNavigationDrawer,
  VToolbar,
  VToolbarTitle,
  VFadeTransition,
  VIcon,
  VSpacer,
  VForm,
  VContainer,
  VLayout,
  VCard,
  VCardTitle,
  VFlex,
  VContent,
  VMenu,
  VList,
  VListTile,
  VListTileTitle,
  VListTileSubTitle,
  VListTileContent,
  VListTileAction,
  VDivider,
  VSelect,
  VProgressCircular,
} from 'vuetify/lib'
import { Ripple } from 'vuetify/lib/directives'
import { ObserveVisibility } from 'vue-observe-visibility'

// Global handler for stale-chunk SyntaxErrors that fire outside Vue Router
// (e.g. during initial page load or non-route dynamic imports).
// router.onError only covers lazy-loaded route resolution — this catches the rest.
window.addEventListener('error', (event) => {
  const msg = `${event.message || ''} ${event.error?.message || ''} ${event.error?.name || ''}`
  const filename = event.filename || ''
  const hasExplicitChunkSignal = /Loading chunk|ChunkLoadError/i.test(msg)
  const hasScriptParseSignal = /Unexpected token\s*<|Function statements must have a name/i.test(msg)
  const looksLikeChunkScript = /(?:\.js(?:$|\?)|\/js\/|chunk)/i.test(filename)
  const isChunkError =
    hasExplicitChunkSignal ||
    (hasScriptParseSignal && event.error?.name === 'SyntaxError' && looksLikeChunkScript)

  if (!isChunkError) return
  try {
    const lastReload = sessionStorage.getItem('chunk_error_reload')
    const now = Date.now()

    if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
      sessionStorage.setItem('chunk_error_reload', now.toString())
      window.location.reload()
    }
  } catch {
    if (!window.name.includes('chunk_reload')) {
      window.name += 'chunk_reload'
      window.location.reload()
    }
  }
})

Vue.directive('observe-visibility', ObserveVisibility)

Vue.use(Vuetify, {
  components: {
    VApp,
    VNavigationDrawer,
    VToolbar,
    VToolbarTitle,
    VFadeTransition,
    VIcon,
    VSpacer,
    VForm,
    VContainer,
    VLayout,
    VCard,
    VCardTitle,
    VFlex,
    VContent,
    VMenu,
    VList,
    VListTile,
    VListTileTitle,
    VListTileSubTitle,
    VListTileContent,
    VListTileAction,
    VDivider,
    VSelect,
    VProgressCircular,
  },
  directives: {
    Ripple,
  },
})

// unsync is optional in case you need to teardown the app
// eslint-disable-next-line
const unsync = sync(store, router)

Vue.config.productionTip = false

Vue.use(VueMq, {
  breakpoints: {
    // default breakpoints - customize this
    sm: 624,
    md: 960,
    ml: 1100,
    lg: 1400,
    xl: Infinity,
  },
  defaultBreakpoint: 'sm', // customize this for SSR
})

Vue.use(Notifications)
Vue.use(vueScrollto)

let vm = new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount('#app')

// Initialize heartbeat system for real-time user validation
try {
  initHeartbeat(store)
  console.log('✅ Heartbeat system initialized successfully')
  
  // Watch authentication status and manage heartbeat lifecycle  
  store.watch(
    (state) => state.user.isAuthenticated,
    (isAuthenticated) => {
      if (isAuthenticated) {
        console.log('🔐 User authenticated - starting heartbeat monitoring')
        try {
          startHeartbeat()
        } catch (error) {
          console.warn('⚠️ Failed to start heartbeat:', error.message)
        }
      } else {
        console.log('🔓 User logged out - stopping heartbeat monitoring')
        try {
          stopHeartbeat()
        } catch (error) {
          console.warn('⚠️ Failed to stop heartbeat:', error.message)
        }
      }
    },
    { immediate: true }
  )

  // Start heartbeat if user is already authenticated on app load
  if (store.getters.isAuthenticated) {
    console.log('🔐 User already authenticated on load - starting heartbeat monitoring')
    try {
      startHeartbeat()
    } catch (error) {
      console.warn('⚠️ Failed to start heartbeat on load:', error.message)
    }
  }
} catch (error) {
  console.error('❌ Failed to initialize heartbeat system:', error.message)
  console.log('📝 App will continue without heartbeat monitoring')
}

// Expose vm to the window if we're not in prod mode
if (process.env.NODE_ENV !== 'production') {
  window.vm = vm
}
